import {cn} from '@tempmee/core'
import {type ElementType, type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {Logo} from '../logo'
import {Tab, type TabProps, Tabs} from '../tabs'
import {TopNavVariantsFactory} from './Variants'

export interface TopNavItem extends Omit<TabProps, 'children'> {
  /**
   * Optional trailing node (e.g. a notification `<Label severity="flagged">`).
   * Everything else — `icon`, `iconPosition`, `disabled`, `href`, `component`,
   * `onClick`, `data-*`, `aria-*` — is forwarded to the underlying `Tab`.
   */
  badge?: ReactNode
  /** Arbitrary `data-*` attributes (e.g. test hooks) forwarded to the Tab. */
  [dataAttr: `data-${string}`]: unknown
}

export interface TopNavProps extends Omit<HTMLAttributes<HTMLElement>, 'onChange'> {
  /**
   * `light` — faint brand bar with dark content; `dark` — deep teal
   * (`primary-contrast`) bar with white content and the flat white wordmark.
   * Pair with `<Sidebar color="dark">` for a single themed shell.
   * @default 'light'
   */
  color?: 'light' | 'dark'
  /** Navigation items rendered as tabs on the right. Optional — omit and use slots for a fully custom bar. */
  items?: TopNavItem[]
  /** Active item value (controlled). */
  value?: string
  /** Initially active item (uncontrolled). */
  defaultValue?: string
  /** Called with the value of the activated item. */
  onChange?: (value: string) => void
  /** Override the default Logo node. */
  logo?: ReactNode
  /**
   * Render the logo inside a custom link element (e.g. a router `Link`) for
   * client-side navigation. Defaults to `a` when `logoHref` is set.
   */
  logoComponent?: ElementType
  /** Wrap the logo in a link with this href. */
  logoHref?: string
  /** Extra props for the logo link/component (e.g. `to` for a router `Link`). */
  logoProps?: Record<string, unknown>
  /** Content rendered at the far left, *before* the logo (e.g. a drawer/collapse
   * hamburger — the MUI app-bar position). Distinct from `startSlot`, which sits
   * to the right of the logo. */
  leadingSlot?: ReactNode
  /** Content rendered to the right of the logo, before the nav items (e.g. a search box). */
  startSlot?: ReactNode
  /** Content rendered at the far right, after the nav items (e.g. an account menu). */
  endSlot?: ReactNode
}

/**
 * The desktop application top bar: a Logo on the left and a row of `Tab`-style
 * navigation items on the right, with `startSlot`/`endSlot` escape hatches for
 * bespoke bars (search, account menus, custom links). Replaces the former `Navbar`.
 */
export const TopNav = forwardRef<HTMLElement, TopNavProps>(
  (
    {
      color = 'light',
      items = [],
      value,
      defaultValue,
      onChange,
      logo,
      logoComponent,
      logoHref,
      logoProps,
      leadingSlot,
      startSlot,
      endSlot,
      className,
      ...props
    },
    ref,
  ) => {
    const logoNode = logo ?? (
      // Explicit width so the wordmark self-sizes — the logo frame is now `min-w`,
      // so it no longer caps the SVG's default `width:100%` at 82px.
      // On the dark bar the brand is the flat white wordmark (`solid` + `light`),
      // not the mint gradient — per Figma Admin-Panel v3.
      <Logo
        tone={color === 'dark' ? 'light' : 'dark'}
        appearance={color === 'dark' ? 'solid' : 'gradient'}
        mark="full"
        aria-label="GoTu home"
        className="w-[82px]"
      />
    )

    const LogoWrapper: ElementType | undefined =
      logoComponent ?? (logoHref != null ? 'a' : undefined)
    const wrappedLogo = LogoWrapper ? (
      <LogoWrapper
        href={logoHref}
        className="flex items-center"
        aria-label="GoTu home"
        {...logoProps}
      >
        {logoNode}
      </LogoWrapper>
    ) : (
      logoNode
    )

    return (
      <header ref={ref} className={cn(TopNavVariantsFactory({color}), className)} {...props}>
        <div className="flex h-full items-center gap-4">
          {leadingSlot}
          {/* `min-w` so the standard wordmark keeps its 82px frame, but a wider
              custom brand (e.g. a monogram + product-name lockup) can grow to fit. */}
          <div className="flex h-full min-w-[82px] shrink-0 items-center">{wrappedLogo}</div>
          {startSlot}
        </div>

        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <Tabs
              value={value}
              defaultValue={defaultValue}
              onChange={onChange}
              // No tablist divider in the bar; force readable tab color per theme.
              className={cn(
                '[&_[role=tablist]]:border-0',
                color === 'dark' && '[&_[role=tab]]:text-white [&_[role=tab]:hover]:text-white',
              )}
            >
              {items.map(({badge, label, value: itemValue, ...tabRest}) => (
                <Tab
                  key={itemValue}
                  value={itemValue}
                  label={
                    badge ? (
                      <span className="inline-flex items-center gap-2">
                        {label}
                        {badge}
                      </span>
                    ) : (
                      label
                    )
                  }
                  {...tabRest}
                />
              ))}
            </Tabs>
          )}
          {endSlot}
        </div>
      </header>
    )
  },
)

TopNav.displayName = 'TopNav'
